import { TAuthResponse, TAuthStep } from "franken-srp";
import { useRef, useState } from "react";

import { setDevice } from "@smart/auth-srp-dom";
import { Provider, ProviderKey } from "@smart/bridge-providers-dom";
import { displayName, LocalAWSError } from "@smart/itops-utils-basic";

import { authApi } from "./api";

export type LoginEvents = {
  region: string;
  clientId: string;
  provider: Provider<ProviderKey>;
  onComplete?: (response: TAuthResponse) => void;
  onError?: (error: Error) => void;
};

export type Setters = {
  setStep: (newStep: TAuthStep | undefined) => void;
  setEmail: (newEmail: string) => void;
};

const buildHint = (
  provider: Provider<ProviderKey>,
  step: TAuthStep | undefined,
) => {
  switch (step?.code) {
    case "SMS_MFA_REQUIRED":
      return `Your account is protected with multi-factor authentication using SMS. Enter the code sent to ${step?.hint} to continue:`;
    case "SOFTWARE_MFA_REQUIRED":
      return `Your account is protected with multi-factor authentication using a one-time code. Enter the code from your authenticator app to continue:`;
    case "NEW_PASSWORD_REQUIRED":
      return "Your password has been reset. Enter a new password to continue:";
    case "TOKENS":
      return "";
    default:
      return (
        step?.hint ||
        `Login to ${displayName} with your ${provider.label} account. Enter your
        username and password to continue:`
      );
  }
};

export const useAuthStep = ({
  region,
  clientId,
  provider,
  onComplete,
  onError,
}: LoginEvents): {
  step: TAuthStep | undefined;
  hint: string | undefined;
} & Setters => {
  const stash = authApi.stash();
  const email = useRef("");
  const [hint, setHint] = useState<string>(buildHint(provider, undefined));
  const [step, setStep] = useState<TAuthStep>();

  return {
    step,
    hint,
    setEmail: (newEmail) => {
      email.current = newEmail;
    },
    setStep: (input) => {
      const newStep: TAuthStep | undefined = input && { ...input };
      if (
        newStep?.error?.message === "Incorrect username or password." ||
        newStep?.error?.message === "User does not exist."
      ) {
        newStep.error = new LocalAWSError(
          "NotAuthorizedException",
          newStep.error.message,
          400,
          "ERR-2024",
        );
      }
      if (newStep?.error?.message.match(/mfa code/i)) {
        newStep.error = new LocalAWSError(
          "MFA",
          newStep.error.message,
          400,
          "ERR-2025",
        );
      }

      setHint(buildHint(provider, newStep));
      setStep(newStep);
      if (newStep?.code === "TOKENS" && newStep.response) {
        if (onComplete) onComplete(newStep.response);

        const {
          username,
          newDevice,
          tokens: { refreshToken },
        } = newStep.response;

        if (newDevice && newDevice.password) {
          setDevice(username, { ...newDevice, password: newDevice.password });
        }

        stash
          .post({
            body: {
              region,
              clientId,
              username,
              email: email.current,
              token: refreshToken,
              provider: provider.key,
            },
            token: undefined,
            events: {
              onResult: () => {
                window.location.href = "/api/recall";
              },
            },
          })
          .catch(console.error);
      }
      if (newStep?.code === "ERROR" && newStep?.error && onError) {
        onError(newStep.error);
      }
    },
  };
};
